// pwa — Bloom as an installable app. Registers the service worker (served by BloomAppService
// from /api/bloom/sw.js, scope "/") and turns the browser's install offer into a hook.
import { useEffect, useState } from "react";
import { signal } from "./pulse";

type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

// "prompt": the browser can install (Android Chrome, desktop Chrome/Edge).
// "ios": iPhone/iPad Safari — install is Share → Add to Home Screen.
// "android": Android browser without an install offer — use the browser menu.
// "bookmark": desktop without an install offer; a bookmark is the next best thing.
// "installed": already running as the app.
export type InstallMode = "prompt" | "ios" | "android" | "bookmark" | "installed";

let offer: InstallEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((f) => f());

function standalone(): boolean {
    try {
        return window.matchMedia("(display-mode: standalone)").matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
    } catch (e) {
        return false;
    }
}

function isAndroid(): boolean {
    return /android/i.test(navigator.userAgent || "");
}

// iPadOS reports itself as a Mac; a touch screen gives it away.
function isIos(): boolean {
    const ua = navigator.userAgent || "";
    return !isAndroid() && (/iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
}

// Call once, before the app mounts: the install offer can fire before React is ready.
export function registerPwa() {
    try {
        window.addEventListener("beforeinstallprompt", (e) => {
            e.preventDefault();
            offer = e as InstallEvent;
            notify();
        });
        window.addEventListener("appinstalled", () => {
            signal("app_installed");
            installed = true;
            offer = null;
            notify();
        });
        if ("serviceWorker" in navigator) {
            const go = () => navigator.serviceWorker.register("/api/bloom/sw.js", { scope: "/" }).catch(() => {});
            document.readyState === "complete" ? go() : window.addEventListener("load", go, { once: true });
        }
    } catch (e) {}
}

function currentMode(): InstallMode {
    if (installed || standalone()) return "installed";
    if (offer) return "prompt";
    if (isIos()) return "ios";
    if (isAndroid()) return "android";
    return "bookmark";
}

export function useInstall(): { mode: InstallMode; install: () => Promise<boolean> } {
    const [mode, setMode] = useState<InstallMode>(currentMode);
    useEffect(() => {
        const update = () => setMode(currentMode());
        listeners.add(update);
        update();
        return () => { listeners.delete(update); };
    }, []);
    const install = async () => {
        if (!offer) return false;
        const e = offer;
        offer = null;
        try {
            await e.prompt();
            const choice = await e.userChoice;
            if (choice.outcome === "accepted") installed = true;
        } catch (err) {}
        notify();
        return installed;
    };
    return { mode, install };
}
